import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronDown, Search, X } from "lucide-react";
import { useDeferredValue } from "react";
import { useSearchParams } from "react-router-dom";
import { InfiniteScrollTrigger } from "../components/InfiniteScrollTrigger";
import { ProductCard } from "../components/ProductCard";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { displayName } from "../lib/format";
import type { ProductSummary } from "../lib/types";

type Category = { publicId: string; name: string; slug: string; description?: string | null };
type ProductPage = { items: ProductSummary[]; total: number; limit: number; offset: number; hasMore: boolean };

export function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const deferredSearch = useDeferredValue(search);
  const category = params.get("category") ?? "";
  const sort = params.get("sort") ?? "featured";
  const query = new URLSearchParams({ ...(deferredSearch && { search: deferredSearch }), ...(category && { category }), sort });
  const products = useInfiniteQuery({
    queryKey: ["products", query.toString()],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<ProductPage>(`/products?${query}&limit=24&offset=${pageParam}`),
    getNextPageParam: (last) => last.hasMore ? last.offset + last.items.length : undefined
  });
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => api<Category[]>("/categories") });
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: key === "search" });
  };
  const items = products.data?.pages.flatMap((page) => page.items) ?? [];
  const total = products.data?.pages[0]?.total;
  const active = categories.data?.find((item) => item.slug === category);
  const title = active ? displayName(active.name) : "The shop";
  const intro = active?.description ?? "Miniatures, display shelves, and tiny keepsakes. Every piece lists how long it takes to make.";

  return (
    <section className="container-page pb-10 pt-10 md:pt-16">
      <Seo title={active ? displayName(active.name) : "Shop"} description="Browse dollhouse miniatures, 3D-printed display shelves, and handmade keepsakes, made in small batches." path="/shop" />

      <div className="flex flex-wrap items-end gap-x-5 gap-y-2">
        <h1 className="heading">{title}</h1>
        {total !== undefined && <p className="pb-2 font-hand text-2xl leading-none text-ribbon-ink">{total} {total === 1 ? "piece" : "pieces"}</p>}
      </div>
      <p className="mt-4 max-w-[52ch] text-muted">{intro}</p>

      <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center">
        <label className="relative flex-1 md:max-w-md">
          <span className="sr-only">Search the shop</span>
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={18} />
          <input type="search" className="field rounded-full pl-11 pr-11 [&::-webkit-search-cancel-button]:hidden" value={search} onChange={(e) => set("search", e.target.value)} placeholder="Search cakes, shelves, plates…" />
          {search && <button type="button" onClick={() => set("search", "")} aria-label="Clear search" className="absolute right-2 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full text-muted hover:bg-frosting hover:text-cocoa"><X size={16} /></button>}
        </label>
        <label className="relative md:ml-auto">
          <span className="sr-only">Sort products</span>
          <select className="field appearance-none rounded-full border-transparent bg-frosting py-2.5 pl-4 pr-10 text-[15px] font-bold hover:border-transparent md:w-auto" value={sort} onChange={(e) => set("sort", e.target.value)}>
            <option value="featured">Sort: featured first</option>
            <option value="newest">Sort: newest</option>
            <option value="price-asc">Sort: price, low to high</option>
            <option value="price-desc">Sort: price, high to low</option>
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-cocoa" />
        </label>
      </div>

      <div className="-mx-4 mt-5 overflow-x-auto px-4 pb-2 [scrollbar-width:none] sm:mx-0 sm:px-0" role="group" aria-label="Filter by category">
        <div className="flex w-max gap-2">
          <CategoryChip label="Everything" selected={!category} onClick={() => set("category", "")} />
          {categories.data?.map((item) => <CategoryChip key={item.publicId} label={displayName(item.name)} selected={category === item.slug} onClick={() => set("category", item.slug)} />)}
        </div>
      </div>

      <div className="ruler mt-6" aria-hidden="true" />

      {products.isError && <p className="mt-10 text-muted">The shop didn’t load. Check your connection, then refresh the page.</p>}
      {!products.isLoading && !products.isError && items.length === 0 && <div className="py-20 text-center">
        <p className="font-display text-3xl text-cocoa">Nothing on this shelf yet</p>
        <p className="mt-3 text-muted">{search ? `No pieces match “${search}”. Try a shorter word.` : "This category is being restocked. Have a look at everything else."}</p>
        <button type="button" className="btn-secondary mt-6" onClick={() => setParams(new URLSearchParams())}>Show everything</button>
      </div>}

      <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-8">
        {products.isLoading && Array.from({ length: 8 }, (_, n) => <div key={n}><div className="aspect-square animate-pulse rounded-[6px] bg-frosting" /><div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-frosting" /></div>)}
        {items.map((product) => <ProductCard key={product.publicId} product={product} showCategory={!category} />)}
      </div>
      <InfiniteScrollTrigger hasMore={products.hasNextPage} loading={products.isFetchingNextPage} onLoadMore={() => void products.fetchNextPage()} />
    </section>
  );
}

function CategoryChip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`min-h-10 whitespace-nowrap rounded-[4px] border-[1.5px] px-3.5 text-[14px] font-bold transition-colors ${selected ? "border-cocoa bg-cocoa text-white" : "border-line bg-white text-cocoa hover:border-cocoa/40"}`}
    >
      {label}
    </button>
  );
}
